import { Injectable, inject } from "@angular/core";
import { Subject } from "rxjs";
import { TrpcService } from "./trpc.service";

export interface UploadProgress {
	stage: "idle" | "requesting-url" | "uploading" | "complete" | "error";
	progress: number;
	objectKey?: string;
	error?: string;
}

export interface UploadResult {
	objectKey: string;
	publicUrl: string;
}

@Injectable({
	providedIn: "root",
})
export class UploadService {
	private readonly trpc = inject(TrpcService);
	private readonly progressSubject = new Subject<UploadProgress>();

	readonly progress$ = this.progressSubject.asObservable();

	private readonly MAX_FILE_SIZE = 10 * 1024 * 1024;
	private readonly ALLOWED_TYPES = [
		"image/jpeg",
		"image/jpg",
		"image/png",
		"image/webp",
		"image/gif",
	];

	async uploadImage(file: File): Promise<UploadResult> {
		this.emitProgress({ stage: "idle", progress: 0 });
		this.validateFile(file);

		try {
			this.emitProgress({ stage: "requesting-url", progress: 0 });

			const { uploadUrl, objectKey } =
				await this.trpc.client.upload.requestUrl.mutate({
					filename: file.name,
					contentType: file.type as
						| "image/jpeg"
						| "image/jpg"
						| "image/png"
						| "image/webp"
						| "image/gif",
					sizeBytes: file.size,
				});

			this.emitProgress({ stage: "uploading", progress: 0 });
			await this.uploadToR2(file, uploadUrl);

			this.emitProgress({ stage: "complete", progress: 100, objectKey });
			return { objectKey, publicUrl: objectKey };
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : "Upload failed";
			this.emitProgress({ stage: "error", progress: 0, error: errorMessage });
			throw error;
		}
	}

	private validateFile(file: File): void {
		if (file.size > this.MAX_FILE_SIZE) {
			throw new Error(
				`File too large. Maximum size is ${this.MAX_FILE_SIZE / 1024 / 1024}MB`,
			);
		}
		if (!this.ALLOWED_TYPES.includes(file.type)) {
			throw new Error(
				`Invalid file type. Allowed types: ${this.ALLOWED_TYPES.join(", ")}`,
			);
		}
	}

	private uploadToR2(file: File, uploadUrl: string): Promise<void> {
		return new Promise((resolve, reject) => {
			const xhr = new XMLHttpRequest();

			xhr.upload.addEventListener("progress", (event) => {
				if (event.lengthComputable) {
					const progress = Math.round((event.loaded / event.total) * 100);
					this.emitProgress({ stage: "uploading", progress });
				}
			});

			xhr.addEventListener("load", () => {
				if (xhr.status >= 200 && xhr.status < 300) {
					resolve();
				} else {
					reject(
						new Error(
							`Upload failed with status ${xhr.status}: ${xhr.statusText}`,
						),
					);
				}
			});

			xhr.addEventListener("error", () =>
				reject(new Error("Network error during upload")),
			);
			xhr.addEventListener("abort", () => reject(new Error("Upload aborted")));

			xhr.open("PUT", uploadUrl);
			xhr.setRequestHeader("Content-Type", file.type);
			xhr.send(file);
		});
	}

	private emitProgress(progress: UploadProgress): void {
		this.progressSubject.next(progress);
	}
}
